import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useClient } from "utils/client";
import { CopyProject, CopyUser, ProjectCopyRequest, ProjectCopySummary } from "pages/Backoffice/ProjectCopy/types";

const root = "/admin/project-copies";
const requestOptions = { timeout: 115000 };
export const copyOperationKey = (id: string) => ["project-copy-operation", id];
export const copyErrorMessage = (error: unknown) => {
  const e = error as AxiosError<{ message?: string }>;
  return e?.response?.data?.message || e?.message || "Request failed";
};
const forbiddenOrMissing = (error: unknown) =>
  [403, 404].includes((error as AxiosError)?.response?.status || 0);

export const useCopyDiscovery = (sourceAccount: string) => {
  const client = useClient();
  const options = { retry: false, refetchOnWindowFocus: false } as const;
  const users = useQuery<CopyUser[]>(["project-copy-users"], async () =>
    (await client.get("/cognito")).data, options);
  const projects = useQuery<CopyProject[]>(["project-copy-projects", sourceAccount], async () =>
    (await client.get("/admin/project-copy/projects", { params: { accountId: sourceAccount } })).data,
    { ...options, enabled: !!sourceAccount });
  return { users, projects };
};

export const usePreviewProjectCopy = () => {
  const client = useClient();
  return useMutation<ProjectCopySummary, unknown, ProjectCopyRequest>(async (payload) =>
    (await client.post(root, payload, requestOptions)).data, { retry: false });
};

export const useProjectCopy = (id: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const currentId = useRef(id);
  currentId.current = id;
  const actionLock = useRef(false);
  const readGeneration = useRef(0);
  const [pendingId, setPendingId] = useState("");
  const [uncertainId, setUncertainId] = useState("");
  const [actionError, setActionError] = useState<{ id: string; message: string }>();
  const pending = !!id && pendingId === id;
  const uncertain = !!id && uncertainId === id;
  const fetchSummary = async () => (await client.get(`${root}/${encodeURIComponent(id)}`, requestOptions)).data as ProjectCopySummary;
  const operation = useQuery<ProjectCopySummary>(copyOperationKey(id), fetchSummary, {
    enabled: !!id && !pending,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (data, query) => {
      if (pending || forbiddenOrMissing(query.state.error)) return false;
      return uncertain || data?.status === "copy" || data?.status === "cleanup" ? 5000 : false;
    },
    onSuccess: () => {
      if (currentId.current === id && !actionLock.current) setUncertainId((value) => value === id ? "" : value);
    },
  });

  const action = useMutation<ProjectCopySummary, unknown, "apply" | "cleanup">(
    async (name) => (await client.post(`${root}/${encodeURIComponent(id)}/${name}`,
      name === "apply" ? { sourceQuiesced: true } : {}, requestOptions)).data,
    { retry: false },
  );

  const refresh = async () => {
    if (!id || actionLock.current) return;
    const generation = ++readGeneration.current;
    await queryClient.cancelQueries(copyOperationKey(id));
    try {
      await queryClient.fetchQuery(copyOperationKey(id), fetchSummary, { retry: false, staleTime: 0 });
      if (currentId.current === id && generation === readGeneration.current && !actionLock.current) setUncertainId((value) => value === id ? "" : value);
    } catch {
      // Keep uncertainty until a fresh GET succeeds. The query exposes read failures.
    }
  };

  const execute = async (name: "apply" | "cleanup") => {
    const summary = operation.data;
    if (!id || actionLock.current || uncertain || operation.isError || operation.isFetching ||
      !(name === "apply" ? summary?.canApply : summary?.canCleanup)) return;
    actionLock.current = true;
    ++readGeneration.current;
    setPendingId(id);
    setActionError(undefined);
    setUncertainId(id);
    await queryClient.cancelQueries(copyOperationKey(id));
    try {
      const result = await action.mutateAsync(name);
      // Discard any GET that started before the POST finished.
      await queryClient.cancelQueries(copyOperationKey(id));
      queryClient.setQueryData(copyOperationKey(id), result);
    } catch (error) {
      setActionError({ id, message: copyErrorMessage(error) });
    } finally {
      actionLock.current = false;
      setPendingId("");
      await queryClient.invalidateQueries(copyOperationKey(id), { refetchActive: false });
      if (currentId.current === id) await refresh();
    }
  };
  return { operation, pending, uncertain, actionError: actionError?.id === id ? actionError.message : undefined, execute, refresh };
};
